(() => {
  const SELECTOR = '[data-customer-moments]';
  const ROOT_MARGIN = '400px 0px';

  class CustomerMoments {
    constructor(root) {
      this.root = root;
      this.videos = root.querySelector('.cmm__videos');
      this.featuredTrigger = root.querySelector('.cmm__featured[data-cmm-trigger]');
      this.activeTrigger = null;
      this.inlinePlayer = null;
      this.inlineMedia = null;
      this.inlineProduct = null;
      this.inlineProductLink = null;
      this.inlineProductTitle = null;

      if (!this.videos || !this.featuredTrigger) return;

      this.hydrateThumbnails();
      this.bind();
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const closeButton = event.target.closest('[data-cmm-inline-close]');
        if (closeButton && this.root.contains(closeButton)) {
          event.preventDefault();
          this.stopInlinePlayback();
          return;
        }

        const trigger = event.target.closest('[data-cmm-trigger]');
        if (!trigger || !this.root.contains(trigger)) return;

        event.preventDefault();
        this.playInline(trigger);
      });
    }

    createInlinePlayer() {
      if (this.inlinePlayer || !this.videos || !this.featuredTrigger) return;

      const player = document.createElement('div');
      player.className = 'cmm__inline-player';
      player.hidden = true;
      player.setAttribute('data-cmm-inline-player', '');

      const media = document.createElement('div');
      media.className = 'cmm__inline-media';
      media.setAttribute('data-cmm-inline-media', '');

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'cmm__inline-close';
      close.setAttribute('data-cmm-inline-close', '');
      close.setAttribute('aria-label', 'Close video');
      close.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';

      const product = document.createElement('div');
      product.className = 'cmm__inline-product';
      product.hidden = true;
      product.setAttribute('data-cmm-inline-product', '');

      const productTitle = document.createElement('span');
      productTitle.className = 'cmm__inline-product-title';

      const productLink = document.createElement('a');
      productLink.className = 'cmm__inline-product-link';
      productLink.textContent = 'Shop This Card';

      product.append(productTitle, productLink);
      player.append(media, close, product);
      this.videos.insertBefore(player, this.featuredTrigger);

      this.inlinePlayer = player;
      this.inlineMedia = media;
      this.inlineProduct = product;
      this.inlineProductLink = productLink;
      this.inlineProductTitle = productTitle;
    }

    hydrateThumbnails() {
      this.root.querySelectorAll('[data-cmm-trigger]').forEach((trigger) => {
        const media = trigger.querySelector('.cmm__featured-media, .cmm__support-media');
        const placeholder = media?.querySelector('.cmm__placeholder');
        if (!media || !placeholder) return;

        const provider = trigger.dataset.videoProvider || '';
        const configuredId = trigger.dataset.videoId || '';
        const youtubeId = provider === 'youtube' && configuredId
          ? configuredId
          : this.youtubeId(trigger.dataset.videoUrl || '');

        if (!youtubeId) return;

        const encodedId = encodeURIComponent(youtubeId);
        const image = document.createElement('img');
        image.className = 'cmm__image';
        image.alt = trigger.dataset.title || 'Customer video';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.width = 480;
        image.height = 360;
        image.src = `https://i.ytimg.com/vi/${encodedId}/hqdefault.jpg`;
        image.onerror = () => {
          image.onerror = null;
          image.src = `https://i.ytimg.com/vi/${encodedId}/0.jpg`;
        };
        placeholder.replaceWith(image);
      });
    }

    playInline(trigger) {
      this.createInlinePlayer();
      if (!this.inlinePlayer || !this.inlineMedia) return;

      const videoUrl = trigger.dataset.videoUrl;
      const videoId = trigger.dataset.videoId;
      if (!videoUrl && !videoId) return;

      this.stopCurrentMedia();
      this.activeTrigger = trigger;

      this.featuredTrigger.hidden = true;
      this.inlinePlayer.hidden = false;
      this.renderVideo(trigger);
      this.renderProduct(trigger);
      this.updateActiveCard(trigger);
    }

    stopInlinePlayback() {
      this.stopCurrentMedia();
      this.activeTrigger = null;

      if (this.inlinePlayer) this.inlinePlayer.hidden = true;
      if (this.featuredTrigger) this.featuredTrigger.hidden = false;
      if (this.inlineProduct) this.inlineProduct.hidden = true;

      this.updateActiveCard(null);
      this.featuredTrigger?.focus({ preventScroll: true });
    }

    stopCurrentMedia() {
      const video = this.inlineMedia?.querySelector('video');
      if (video) video.pause();
      if (this.inlineMedia) this.inlineMedia.innerHTML = '';
    }

    renderVideo(trigger) {
      const type = trigger.dataset.videoType;
      const rawUrl = trigger.dataset.videoUrl || '';
      const provider = trigger.dataset.videoProvider || '';
      const videoId = trigger.dataset.videoId || '';
      const poster = trigger.dataset.videoPoster || '';

      if (type === 'hosted') {
        const url = this.normaliseUrl(rawUrl);
        if (!url) return;

        const video = document.createElement('video');
        video.className = 'cmm__video-element';
        video.src = url;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.setAttribute('playsinline', '');
        if (poster) video.poster = this.normaliseUrl(poster);

        this.inlineMedia.append(video);
        video.play().catch(() => {});
        return;
      }

      const embedUrl = this.externalEmbedUrl(provider, videoId, rawUrl);
      if (!embedUrl) return;

      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.title = trigger.dataset.title || 'Customer video';
      iframe.loading = 'eager';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.allowFullscreen = true;

      this.inlineMedia.append(iframe);
    }

    renderProduct(trigger) {
      if (!this.inlineProduct || !this.inlineProductLink || !this.inlineProductTitle) return;

      const productUrl = trigger.dataset.productUrl || '';
      const productTitle = trigger.dataset.productTitle || '';

      if (!productUrl || !productTitle) {
        this.inlineProduct.hidden = true;
        return;
      }

      this.inlineProductTitle.textContent = productTitle;
      this.inlineProductLink.href = productUrl;
      this.inlineProduct.hidden = false;
    }

    updateActiveCard(trigger) {
      this.root.querySelectorAll('.cmm__support-card').forEach((card) => {
        const isActive = card === trigger;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-pressed', String(isActive));
      });
    }

    externalEmbedUrl(provider, videoId, rawUrl) {
      let url = '';

      if (provider === 'youtube' && videoId) {
        url = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`;
      } else if (provider === 'vimeo' && videoId) {
        url = `https://player.vimeo.com/video/${encodeURIComponent(videoId)}`;
      } else {
        url = this.normaliseExternalUrl(rawUrl);
      }

      if (!url) return '';

      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('autoplay', '1');
        parsed.searchParams.set('playsinline', '1');

        if (provider === 'youtube' || parsed.hostname.includes('youtube')) {
          parsed.searchParams.set('rel', '0');
          parsed.searchParams.set('modestbranding', '1');
        }

        return parsed.toString();
      } catch (_error) {
        return url;
      }
    }

    youtubeId(rawUrl) {
      const url = this.normaliseUrl(rawUrl);
      if (!url) return '';

      try {
        const parsed = new URL(url, window.location.origin);
        const hostname = parsed.hostname.replace(/^www\./, '');

        if (hostname === 'youtu.be') {
          return parsed.pathname.split('/').filter(Boolean)[0] || '';
        }

        if (hostname.includes('youtube.com')) {
          if (parsed.pathname.startsWith('/embed/')) {
            return parsed.pathname.split('/').filter(Boolean)[1] || '';
          }
          return parsed.searchParams.get('v') || '';
        }
      } catch (_error) {
        return '';
      }

      return '';
    }

    normaliseExternalUrl(rawUrl) {
      const url = this.normaliseUrl(rawUrl);
      if (!url) return '';

      try {
        const parsed = new URL(url, window.location.origin);
        const hostname = parsed.hostname.replace(/^www\./, '');

        if (hostname === 'youtu.be') {
          const id = parsed.pathname.split('/').filter(Boolean)[0];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }

        if (hostname.includes('youtube.com')) {
          if (parsed.pathname.startsWith('/embed/')) return parsed.toString();
          const id = parsed.searchParams.get('v') || parsed.pathname.split('/').filter(Boolean).pop();
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }

        if (hostname === 'vimeo.com' || hostname === 'player.vimeo.com') {
          if (parsed.pathname.startsWith('/video/')) return parsed.toString();
          const id = parsed.pathname.split('/').filter(Boolean).pop();
          return id ? `https://player.vimeo.com/video/${id}` : '';
        }

        return parsed.toString();
      } catch (_error) {
        return url;
      }
    }

    normaliseUrl(rawUrl) {
      if (!rawUrl) return '';
      const decoded = rawUrl.replace(/&amp;/g, '&').trim();
      return decoded.startsWith('//') ? `${window.location.protocol}${decoded}` : decoded;
    }
  }

  const activateRoot = (root) => {
    if (!root || root.dataset.cmmInitialized === 'true') return;
    root.dataset.cmmInitialized = 'true';
    new CustomerMoments(root);
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          activateRoot(entry.target);
        });
      }, { rootMargin: ROOT_MARGIN, threshold: 0.01 })
    : null;

  const scheduleRoot = (root) => {
    if (!root || root.dataset.cmmInitialized === 'true' || root.dataset.cmmObserved === 'true') return;

    if (window.Shopify?.designMode || !observer) {
      activateRoot(root);
      return;
    }

    root.dataset.cmmObserved = 'true';
    observer.observe(root);
  };

  const init = (scope = document) => {
    if (scope.matches?.(SELECTOR)) scheduleRoot(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(scheduleRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
